import React, { useRef, useState, useEffect } from "react";
import "./image-slider.scss"; // Import the CSS file
import ArrowIcon from "../case-studies/arrow-icon";

const ImageSlider = ({ images }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const sliderRef = useRef<any>(null);
    const imageRefs = useRef<any[]>([]);
    const [enablePreviousButton, setEnablePreviousButton] = useState(false);
    const [enableNextButton, setEnableNextButton] = useState(false);

    useEffect(() => {
        if (sliderRef?.current?.scrollLeft > 0) {
            setEnablePreviousButton(true);
        } else {
            setEnablePreviousButton(false);
        }
    }, [sliderRef?.current?.scrollLeft]);

    useEffect(() => {
        if (sliderRef?.current?.scrollLeft < totalImageWidths - sliderRef?.current?.offsetWidth) {
            setEnableNextButton(true);
        } else {
            setEnableNextButton(false);
        }
    }, [sliderRef?.current?.scrollLeft, sliderRef?.current?.offsetWidth]);

    useEffect(() => {
        const handleResize = () => {
            // Recalculate the offsets when the window is resized
            imageRefs.current.forEach((ref, index) => {
                if (ref) {
                    images[index].offsetLeft = ref.offsetLeft;
                }
            });
        };

        window.addEventListener("resize", handleResize);
        handleResize(); // Initial calculation

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [images]);

    useEffect(() => {
        window.addEventListener('resize', checkWhichButtonsShouldBeEnabled);
        window.addEventListener('keydown', handleKeyDown);
    }, [])

    const checkWhichButtonsShouldBeEnabled = () => {
        if (sliderRef?.current?.scrollLeft > 0) {
            setEnablePreviousButton(true);
        } else {
            setEnablePreviousButton(false);
        }

        if (sliderRef?.current?.scrollLeft < totalImageWidths - sliderRef?.current?.offsetWidth) {
            setEnableNextButton(true);
        } else {
            setEnableNextButton(false);
        }
    };


    const nextSlide = () => {
        const nextIndex = findNextImageIndex(sliderRef.current?.scrollLeft);
        setCurrentIndex(nextIndex);
        sliderRef.current.scrollTo({
            behavior: 'smooth',
            top: 0,
            left: imageRefs.current[nextIndex].offsetLeft,
        });

        if (nextIndex === images.length - 1) {
            setEnableNextButton(false);
        }

        setEnablePreviousButton(true);
        setCurrentIndex(nextIndex);
    };

    const prevSlide = () => {
        const prevIndex = findPreviousImageIndex(sliderRef?.current?.scrollLeft);
        setCurrentIndex(prevIndex);
        sliderRef.current.scrollTo({
            behavior: 'smooth',
            top: 0,
            left: imageRefs.current[prevIndex].offsetLeft,
        });

        if (prevIndex === 0) {
            setEnablePreviousButton(false);
        }

        setEnableNextButton(true);
        setCurrentIndex(prevIndex);
    };

    const findNextImageIndex = (scrollPosition) => {
        let closestIndex = 0;
        // for each image find the next one that is closest to the scroll position

        scrollPosition = Math.round(scrollPosition);
        let currentLeft = 0;
        let found = false;
        images.forEach((image, index) => {
            if (!found && currentLeft > scrollPosition) {
                closestIndex = index;
                found = true;
            }
            currentLeft += image.width;
        });

        return closestIndex;
    };


    const totalImageWidths = images.reduce((acc, image, index) => {
        return image.width + acc;
    }, 0)

    const findPreviousImageIndex = (scrollPosition) => {
        let closestIndex = 0;
        // for each image find the next one that is closest to the scroll position

        scrollPosition = Math.round(scrollPosition);
        let currentLeft = totalImageWidths;
        let found = false;

        for (let i = images.length - 1; i >= 0; i--) {
            if (!found && (currentLeft - images[i].width) < scrollPosition) {
                closestIndex = i;
                found = true;
            }
            currentLeft -= images[i].width;
        }

        return closestIndex;
    };


    const handleMouseDown = (e) => {
        e.preventDefault();
        setIsDragging(true);
        setStartX(e.pageX);
        setScrollLeft(sliderRef?.current?.scrollLeft);
    };

    const handleTouchStart = (e) => {
        setIsDragging(true);
        setStartX(e.touches[0].clientX - sliderRef?.current?.offsetLeft);
        setScrollLeft(sliderRef?.current?.scrollLeft);
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - sliderRef?.current?.offsetLeft;
        const walk = (x - startX); // Adjust the scroll speed
        sliderRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleTouchMove = (e) => {
        if (!isDragging) return;
        //e.preventDefault();
        const x = e.touches[0].clientX - sliderRef?.current?.offsetLeft;
        const walk = (x - startX); // Adjust the scroll speed
        sliderRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleKeyDown = (e) => {
        if (e.key === "ArrowRight") {
            nextSlide();
        } else if (e.key === "ArrowLeft") {
            prevSlide();
        }
    };

    return (
        <div className="image-slider" onKeyDown={handleKeyDown} tabIndex={0}>
            <ArrowIcon className={`slider-arrow slider-left-arrow prev ${enablePreviousButton ? "" : "hidden"}`} onClick={prevSlide} aria-label="Previous button" />
            <div
                className="slider-container"
                ref={sliderRef}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                onTouchStart={handleTouchStart}
                onTouchCancel={handleMouseLeave}
                onTouchEnd={handleMouseUp}
                onTouchMove={handleTouchMove}>
                <div className="slider-track">
                    {images.map((image, index) => (
                        <div
                            key={index}
                            className="carousel-slide"
                            ref={(el) => (imageRefs.current[index] = el)}
                        >
                            <img
                                className="carousel-image"
                                src={image.original}
                                alt={image.alt ? image.alt : `Slide ${index}`}
                                style={{ width: `${image.width}px` }} // Set fixed width for images
                            />
                        </div>
                    ))}
                </div>
            </div>
            <ArrowIcon className={`slider-arrow slider-right-arrow next ${enableNextButton ? "" : "hidden"}`} onClick={nextSlide} aria-label="Next button" />
        </div>
    );
};

export default ImageSlider;